import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ServiceLogEntry, type ServiceLogInput, type MaintenanceScheduleItem, type MaintenanceScheduleInput, type ScheduleStatusItem } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';

const card: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};
const field: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, boxSizing: 'border-box', background: 'var(--color-background)', color: 'var(--color-text)' };
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted,#666)', margin: '10px 0 4px' };
const primaryBtn: React.CSSProperties = { background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer' };
const secondaryBtn: React.CSSProperties = { background: 'none', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', color: 'var(--color-text)' };

function fmtDate(ymd: string): string {
  const d = new Date(ymd + 'T00:00:00');
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The recommended cadence, in words. */
function intervalLabel(e: ServiceLogEntry): string {
  if (e.interval_months) return `Every ${e.interval_months} month${e.interval_months === 1 ? '' : 's'}`;
  if (e.interval_hours) return `Every ${e.interval_hours} hrs`;
  if (e.interval_note) return e.interval_note;
  return '-';
}

/** When the next service is due, worked out from the interval where possible. */
function nextDue(e: ServiceLogEntry): { text: string; overdue: boolean } {
  if (e.interval_months) {
    const d = new Date(e.service_date + 'T00:00:00');
    d.setMonth(d.getMonth() + e.interval_months);
    // Format the Date directly — going via toISOString() would shift the day in
    // timezones ahead of UTC (Brisbane), giving an off-by-one due date.
    return { text: d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }), overdue: d.getTime() < Date.now() };
  }
  if (e.interval_hours) {
    const base = e.hour_meter ?? 0;
    return { text: `at ${base + e.interval_hours} hrs`, overdue: false };
  }
  if (e.interval_note) return { text: e.interval_note, overdue: false };
  return { text: 'Not set', overdue: false };
}

export function ServiceLog() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['service-log'], queryFn: () => api.getServiceLog() });
  const entries = data?.entries ?? [];

  const [editing, setEditing] = useState<ServiceLogEntry | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ServiceLogEntry | null>(null);
  const [tab, setTab] = useState<'history' | 'schedule'>('history');
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteServiceLogEntry(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service-log'] }); setConfirmDelete(null); },
  });

  const tabBtn = (active: boolean): React.CSSProperties => ({
    background: active ? 'var(--color-accent)' : 'transparent',
    color: active ? '#fff' : 'var(--color-text)',
    border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
    borderRadius: 8, padding: '6px 14px', fontWeight: 700, cursor: 'pointer',
  });

  // Maintenance alerts (shared query with the schedule tab).
  const { data: status } = useQuery({ queryKey: ['maintenance-status'], queryFn: () => api.getMaintenanceStatus() });
  const overdue = (status?.items ?? []).filter((i) => i.status === 'overdue');
  const dueSoon = (status?.items ?? []).filter((i) => i.status === 'due_soon');

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 'var(--spacing-3, 16px)' }}>
      <h2 style={{ margin: 0 }}>Service Log</h2>
      <p style={{ color: 'var(--color-text-muted,#666)', marginTop: 4 }}>
        Everything about the equipment: the history of what's been serviced, and the manufacturer's
        recommended maintenance schedule. The new owner uses this to see what's been done and what's due.
      </p>

      {(overdue.length > 0 || dueSoon.length > 0) && (
        <div style={{
          marginTop: 'var(--spacing-2)', borderRadius: 8, padding: 'var(--spacing-3)',
          background: overdue.length ? 'rgba(238,41,55,0.08)' : 'rgba(254,197,83,0.14)',
          border: `1px solid ${overdue.length ? 'var(--color-error,#d33)' : 'var(--color-gold,#FEC553)'}`,
        }}>
          <strong style={{ color: overdue.length ? 'var(--color-error,#d33)' : 'var(--color-text)' }}>
            🔧 Maintenance {overdue.length ? 'overdue' : 'coming up'}
          </strong>
          <span style={{ color: 'var(--color-text-muted,#666)', fontSize: 'var(--font-size-sm)' }}>
            {' '}(est. {status?.estimatedEngineHours != null ? `~${status.estimatedEngineHours} engine hrs` : 'hours unknown'})
          </span>
          <div style={{ marginTop: 6, fontSize: 'var(--font-size-sm)' }}>
            {[...overdue.map((i) => `${i.item} (overdue)`), ...dueSoon.map((i) => `${i.item} (due soon)`)].join(' · ')}
          </div>
          <button style={{ ...secondaryBtn, marginTop: 8, padding: '4px 10px', fontSize: 12 }} onClick={() => setTab('schedule')}>View schedule</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <button style={tabBtn(tab === 'history')} onClick={() => setTab('history')}>Service history</button>
        <button style={tabBtn(tab === 'schedule')} onClick={() => setTab('schedule')}>Maintenance schedule</button>
      </div>

      {tab === 'schedule' ? (
        <MaintenanceScheduleTab />
      ) : (
      <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-3)' }}>
        <button style={primaryBtn} onClick={() => setAdding(true)}>+ Log a service</button>
      </div>
      <section style={{ ...card, marginTop: 'var(--spacing-2)', padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <p style={{ color: 'var(--color-text-muted)', padding: 'var(--spacing-4)' }}>Loading…</p>
        ) : entries.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', padding: 'var(--spacing-4)' }}>No services logged yet. Add the first one.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ background: 'var(--color-navy,#1B365D)', color: '#fff', textAlign: 'left' }}>
                  {['Date', 'Hour meter', 'Item serviced / replaced', 'Interval', 'Next due', ''].map((h, i) => (
                    <th key={i} style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', textAlign: i === 5 ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const due = nextDue(e);
                  return (
                    <tr key={e.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{fmtDate(e.service_date)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{e.hour_meter != null ? `${e.hour_meter} hrs` : '-'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {e.item}
                        {e.notes && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{e.notes}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--color-text-muted)' }}>{intervalLabel(e)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: due.overdue ? 700 : 400, color: due.overdue ? 'var(--color-error,#d33)' : 'var(--color-text)' }}>
                        {due.overdue ? `${due.text} · overdue` : due.text}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button style={{ ...secondaryBtn, padding: '4px 10px', fontSize: 12, marginRight: 6 }} onClick={() => setEditing(e)}>Edit</button>
                        <button style={{ ...secondaryBtn, padding: '4px 10px', fontSize: 12, borderColor: 'var(--color-error,#d33)', color: 'var(--color-error,#d33)' }} onClick={() => setConfirmDelete(e)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {(adding || editing) && (
        <EntryModal
          entry={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['service-log'] }); setAdding(false); setEditing(null); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete this log entry?"
        message={`This permanently removes the "${confirmDelete?.item ?? ''}" service record. This can't be undone.`}
        tone="danger"
        confirmLabel={deleteMut.isPending ? 'Deleting…' : 'Delete entry'}
        onConfirm={() => { if (confirmDelete) deleteMut.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />
      </>
      )}
    </div>
  );
}

/** Per-item "next due" text from its computed status. */
function scheduleDueText(s: ScheduleStatusItem): string {
  const parts: string[] = [];
  if (s.nextDueHours != null) parts.push(`${s.nextDueHours} hrs`);
  if (s.nextDueDate) parts.push(fmtDate(s.nextDueDate));
  if (parts.length) return `Due at ${parts.join(' or ')}`;
  if (s.status === 'no_baseline') return 'Set a baseline';
  return '-';
}
function dueChip(status: ScheduleStatusItem['status']): { text: string; color: string } | null {
  if (status === 'overdue') return { text: 'Overdue', color: 'var(--color-error,#d33)' };
  if (status === 'due_soon') return { text: 'Due soon', color: 'var(--color-gold,#B8860B)' };
  return null;
}

/** The recommended maintenance schedule + engine-hours status, plus the safety warning. */
function MaintenanceScheduleTab() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: ['maintenance-status'], queryFn: () => api.getMaintenanceStatus() });
  const items = status?.items ?? [];
  const [editing, setEditing] = useState<MaintenanceScheduleItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<MaintenanceScheduleItem | null>(null);
  const [usageDraft, setUsageDraft] = useState('');
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['maintenance-status'] }); qc.invalidateQueries({ queryKey: ['maintenance-schedule'] }); };
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteMaintenanceItem(id),
    onSuccess: () => { invalidate(); setConfirmDelete(null); },
  });
  const usageMut = useMutation({
    mutationFn: (pct: number) => api.setEngineUsagePct(pct),
    onSuccess: () => { invalidate(); setUsageDraft(''); },
  });
  const usagePct = status?.usagePct ?? 25;

  return (
    <>
      {/* Engine status */}
      <section style={{ ...card, marginTop: 'var(--spacing-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted,#666)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Estimated engine hours</div>
            <div style={{ fontSize: 28, fontWeight: 800 }}>
              {status?.estimatedEngineHours != null ? `~${status.estimatedEngineHours} hrs` : 'Unknown'}
            </div>
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted,#666)', maxWidth: 460 }}>
            {status?.baseReadingHours != null ? (
              <>Based on the last meter reading of <strong>{status.baseReadingHours} hrs</strong> on {fmtDate(status.baseReadingDate!)}, plus {status.workedHoursSinceReading}h of jobs since, at {usagePct}% engine use.</>
            ) : (
              <>Log a service with an hour-meter reading to start estimating engine hours from job time.</>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>Engine runs ~</span>
          <input type="number" min={1} max={100} style={{ ...field, width: 72, padding: '4px 8px' }} value={usageDraft === '' ? String(usagePct) : usageDraft} onChange={(e) => setUsageDraft(e.target.value)} />
          <span style={{ fontSize: 'var(--font-size-sm)' }}>% of job time.</span>
          {usageDraft !== '' && Number(usageDraft) !== usagePct && (
            <button style={{ ...secondaryBtn, padding: '4px 10px', fontSize: 12 }} disabled={usageMut.isPending} onClick={() => usageMut.mutate(Number(usageDraft))}>Save</button>
          )}
        </div>
      </section>

      <div style={{ background: 'rgba(238,41,55,0.08)', border: '1px solid var(--color-error,#d33)', borderRadius: 8, padding: 'var(--spacing-3)', marginTop: 'var(--spacing-3)', color: 'var(--color-text)' }}>
        <strong style={{ color: 'var(--color-error,#d33)' }}>⚠ Low oil cuts the engine.</strong> Running with low oil triggers the Oil Alert and cuts ignition.
        Do not bypass the system. Damage from running with low oil voids warranty and is the most common cause of GX690 failure in the field.
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--color-text-muted,#666)', fontSize: 'var(--font-size-sm)' }}>Hours are read off the engine hour meter. Use whichever interval comes first.</span>
        <button style={primaryBtn} onClick={() => setAdding(true)}>+ Add item</button>
      </div>

      <section style={{ ...card, marginTop: 'var(--spacing-2)', padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <p style={{ color: 'var(--color-text-muted)', padding: 'var(--spacing-4)' }}>Loading…</p>
        ) : items.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', padding: 'var(--spacing-4)' }}>No schedule items yet. Add the first one.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr style={{ background: 'var(--color-navy,#1B365D)', color: '#fff', textAlign: 'left' }}>
                  {['Item', 'Action', 'Interval', 'Next due', ''].map((h, i) => (
                    <th key={i} style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', textAlign: i === 4 ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const chip = dueChip(it.status);
                  return (
                    <tr key={it.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{it.item}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--color-text-muted)' }}>{it.action || '-'}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{it.interval_text}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {scheduleDueText(it)}
                        {chip && <span style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#fff', background: chip.color, borderRadius: 6, padding: '1px 6px' }}>{chip.text}</span>}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button style={{ ...secondaryBtn, padding: '4px 10px', fontSize: 12, marginRight: 6 }} onClick={() => setEditing(it)}>Edit</button>
                        <button style={{ ...secondaryBtn, padding: '4px 10px', fontSize: 12, borderColor: 'var(--color-error,#d33)', color: 'var(--color-error,#d33)' }} onClick={() => setConfirmDelete(it)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {(adding || editing) && (
        <ScheduleModal
          item={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { invalidate(); setAdding(false); setEditing(null); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete this schedule item?"
        message={`This removes "${confirmDelete?.item ?? ''}" from the maintenance schedule. This can't be undone.`}
        tone="danger"
        confirmLabel={deleteMut.isPending ? 'Deleting…' : 'Delete item'}
        onConfirm={() => { if (confirmDelete) deleteMut.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}

/** Add or edit one maintenance schedule item. */
function ScheduleModal({ item, onClose, onSaved }: { item: MaintenanceScheduleItem | null; onClose: () => void; onSaved: () => void }) {
  const editing = !!item;
  const [name, setName] = useState(item?.item ?? '');
  const [action, setAction] = useState(item?.action ?? '');
  const [interval, setInterval] = useState(item?.interval_text ?? '');
  const [intHours, setIntHours] = useState(item?.interval_hours != null ? String(item.interval_hours) : '');
  const [intMonths, setIntMonths] = useState(item?.interval_months != null ? String(item.interval_months) : '');
  const [lastHours, setLastHours] = useState(item?.last_done_hours != null ? String(item.last_done_hours) : '');
  const [lastDate, setLastDate] = useState(item?.last_done_date ?? '');

  const num = (s: string) => (s.trim() === '' ? null : Number(s));
  const saveMut = useMutation({
    mutationFn: () => {
      const payload: MaintenanceScheduleInput = {
        item: name.trim(), action: action.trim() || null, interval_text: interval.trim(),
        interval_hours: num(intHours), interval_months: num(intMonths),
        last_done_hours: num(lastHours), last_done_date: lastDate || null,
      };
      return editing ? api.updateMaintenanceItem(item!.id, payload) : api.createMaintenanceItem(payload);
    },
    onSuccess: onSaved,
  });
  const canSave = name.trim() && interval.trim() && !saveMut.isPending;

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 24, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, borderRadius: 12, width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{editing ? 'Edit schedule item' : 'Add schedule item'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-muted,#666)' }}>✕</button>
        </div>
        <label style={label}>Item *</label>
        <input style={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Engine oil" />
        <label style={label}>Action</label>
        <input style={field} value={action} onChange={(e) => setAction(e.target.value)} placeholder="e.g. Drain & replace" />
        <label style={label}>Interval (as text) *</label>
        <input style={field} value={interval} onChange={(e) => setInterval(e.target.value)} placeholder="e.g. Every 200 hours or 6 months" />

        <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted,#666)' }}>Alerting (optional)</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted,#888)', margin: '2px 0 4px' }}>Set a number to have the portal flag this item as due. Uses whichever comes first.</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={label}>Every … engine hours</label><input type="number" min={0} style={field} value={intHours} onChange={(e) => setIntHours(e.target.value)} placeholder="e.g. 200" /></div>
          <div><label style={label}>Every … months</label><input type="number" min={0} style={field} value={intMonths} onChange={(e) => setIntMonths(e.target.value)} placeholder="e.g. 6" /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={label}>Last done at (hours)</label><input type="number" min={0} style={field} value={lastHours} onChange={(e) => setLastHours(e.target.value)} placeholder="e.g. 1800" /></div>
          <div><label style={label}>Last done (date)</label><input type="date" style={field} value={lastDate} onChange={(e) => setLastDate(e.target.value)} /></div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted,#888)', marginTop: 4 }}>“Last done” resets automatically when you log a service and tag it to this item.</div>
        {saveMut.isError && <p style={{ color: 'var(--color-error,#d33)', fontSize: 13 }}>{(saveMut.error as Error)?.message || 'Could not save.'}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button onClick={() => saveMut.mutate()} disabled={!canSave} style={{ ...primaryBtn, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}>
            {saveMut.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add item'}
          </button>
        </div>
      </div>
    </div>
  );
}

type Unit = 'months' | 'years' | 'hours';

/** Add or edit one service log entry. */
function EntryModal({ entry, onClose, onSaved }: { entry: ServiceLogEntry | null; onClose: () => void; onSaved: () => void }) {
  const editing = !!entry;
  const [serviceDate, setServiceDate] = useState(entry?.service_date ?? new Date().toISOString().slice(0, 10));
  const [hourMeter, setHourMeter] = useState(entry?.hour_meter != null ? String(entry.hour_meter) : '');
  const [item, setItem] = useState(entry?.item ?? '');
  const [intervalValue, setIntervalValue] = useState(
    entry?.interval_hours != null ? String(entry.interval_hours) : entry?.interval_months != null ? String(entry.interval_months) : '',
  );
  const [unit, setUnit] = useState<Unit>(entry?.interval_hours != null ? 'hours' : 'months');
  const [intervalNote, setIntervalNote] = useState(entry?.interval_note ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [scheduleItemId, setScheduleItemId] = useState<string>(entry?.schedule_item_id != null ? String(entry.schedule_item_id) : '');
  const { data: sched } = useQuery({ queryKey: ['maintenance-schedule'], queryFn: () => api.getMaintenanceSchedule() });

  const build = (): ServiceLogInput => {
    const n = Number(intervalValue);
    const hasNum = intervalValue.trim() !== '' && Number.isFinite(n) && n > 0;
    return {
      service_date: serviceDate,
      hour_meter: hourMeter.trim() === '' ? null : Number(hourMeter),
      item: item.trim(),
      interval_months: hasNum && unit === 'months' ? n : hasNum && unit === 'years' ? n * 12 : null,
      interval_hours: hasNum && unit === 'hours' ? n : null,
      interval_note: intervalNote.trim() || null,
      notes: notes.trim() || null,
      schedule_item_id: scheduleItemId ? Number(scheduleItemId) : null,
    };
  };

  const saveMut = useMutation({
    mutationFn: () => (editing ? api.updateServiceLogEntry(entry!.id, build()) : api.createServiceLogEntry(build())),
    onSuccess: onSaved,
  });

  const canSave = serviceDate && item.trim() && !saveMut.isPending;

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 24, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, borderRadius: 12, width: '100%', maxWidth: 520 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{editing ? 'Edit service' : 'Log a service'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-muted,#666)' }}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={label}>Date *</label><input type="date" style={field} value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} /></div>
          <div><label style={label}>Hour meter</label><input type="number" min={0} style={field} value={hourMeter} onChange={(e) => setHourMeter(e.target.value)} placeholder="e.g. 1800" /></div>
        </div>

        <label style={label}>Item serviced / replaced *</label>
        <textarea style={{ ...field, minHeight: 56, resize: 'vertical' }} value={item} onChange={(e) => setItem(e.target.value)} placeholder="e.g. Honda Oil Filter, Air Filter, Oil" />

        <label style={label}>Recommended interval</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 10 }}>
          <input type="number" min={0} style={field} value={intervalValue} onChange={(e) => setIntervalValue(e.target.value)} placeholder="e.g. 3" />
          <select style={field} value={unit} onChange={(e) => setUnit(e.target.value as Unit)}>
            <option value="months">Months</option>
            <option value="years">Years</option>
            <option value="hours">Engine hours</option>
          </select>
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted,#888)', marginTop: 4 }}>
          Sets the “Next due” automatically. Leave blank for a fuzzy cadence and use the note below instead.
        </div>

        <label style={label}>Interval note <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional, for ranges like “every 2-3 years”)</span></label>
        <input style={field} value={intervalNote} onChange={(e) => setIntervalNote(e.target.value)} placeholder="e.g. Replace every 2-3 years" />

        <label style={label}>Resets which scheduled item? <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
        <select style={field} value={scheduleItemId} onChange={(e) => setScheduleItemId(e.target.value)}>
          <option value="">None</option>
          {(sched?.items ?? []).map((s) => <option key={s.id} value={s.id}>{s.item}{s.action ? ` (${s.action})` : ''}</option>)}
        </select>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted,#888)', marginTop: 4 }}>Tagging a scheduled item resets its due date/hours from this service.</div>

        <label style={label}>Notes</label>
        <textarea style={{ ...field, minHeight: 50, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />

        {saveMut.isError && <p style={{ color: 'var(--color-error,#d33)', fontSize: 13 }}>{(saveMut.error as Error)?.message || 'Could not save.'}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button onClick={() => saveMut.mutate()} disabled={!canSave} style={{ ...primaryBtn, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}>
            {saveMut.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add to log'}
          </button>
        </div>
      </div>
    </div>
  );
}
